import { getOpenAIClient } from "@/lib/openai";
import { supabase } from "@/lib/supabase";
import type { ExistingFile } from "../types";

export const uploadFileToAssistant = async (
  file: File,
  vectorStoreId: string,
  assistantId?: string
): Promise<ExistingFile> => {
  console.log('Uploading file to OpenAI:', file.name);
  
  try {
    const openai = await getOpenAIClient();
    
    // 1. Upload file to OpenAI
    const uploadedFile = await openai.files.create({
      file,
      purpose: 'assistants'
    });
    console.log('File uploaded to OpenAI:', uploadedFile);

    // 2. Get the OpenAI vector store ID from our database
    const { data: vectorStore, error: vectorStoreError } = await supabase
      .from('vector_stores')
      .select('openai_vector_store_id')
      .eq('id', vectorStoreId)
      .single();

    if (vectorStoreError) {
      console.error('Error fetching vector store:', vectorStoreError);
      throw vectorStoreError;
    }

    // 3. Add file to the vector store in OpenAI using fetch API
    console.log('Adding file to vector store:', vectorStore.openai_vector_store_id);
    
    const addFileResponse = await fetch(`https://api.openai.com/v1/vector_stores/${vectorStore.openai_vector_store_id}/files`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${openai.apiKey}`,
        'Content-Type': 'application/json',
        'OpenAI-Beta': 'assistants=v2'
      },
      body: JSON.stringify({
        file_id: uploadedFile.id
      })
    });

    if (!addFileResponse.ok) {
      const errorText = await addFileResponse.text();
      throw new Error(`Failed to add file to vector store: ${errorText}`);
    }
    
    console.log('File added to vector store, polling for completion...');
    
    // 4. Poll for file processing (up to 30 seconds)
    let attempts = 0;
    const maxAttempts = 15;
    let fileComplete = false;
    
    while (!fileComplete && attempts < maxAttempts) {
      attempts++;
      
      const fileStatusResponse = await fetch(`https://api.openai.com/v1/vector_stores/${vectorStore.openai_vector_store_id}/files/${uploadedFile.id}`, {
        headers: {
          'Authorization': `Bearer ${openai.apiKey}`,
          'OpenAI-Beta': 'assistants=v2'
        }
      });
      
      if (!fileStatusResponse.ok) {
        throw new Error(`Failed to check file status: ${fileStatusResponse.statusText}`);
      }
      
      const fileStatus = await fileStatusResponse.json();
      console.log(`File status (attempt ${attempts}):`, fileStatus.status);
      
      if (fileStatus.status === 'completed') {
        fileComplete = true;
      } else if (fileStatus.status === 'failed') {
        throw new Error(`File processing failed: ${fileStatus.last_error || 'Unknown error'}`);
      } else {
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }
    
    if (!fileComplete) {
      console.warn('File processing polling timed out, file may not be fully processed yet');
    }

    // 5. Save file metadata to our database
    const { data: fileData, error: fileError } = await supabase
      .from('vector_store_files')
      .insert({
        file_name: file.name,
        file_type: file.type,
        file_size: file.size,
        openai_file_id: uploadedFile.id,
        vector_store_id: vectorStoreId,
        status: 'completed'
      })
      .select()
      .single();

    if (fileError) {
      console.error('Error saving file metadata:', fileError);
      throw fileError;
    }

    // 6. If we have an assistantId, create the association 
    if (assistantId) {
      console.log('Creating assistant file association:', { assistantId, fileId: fileData.id });
      
      const { error: assocError } = await supabase
        .from('assistant_files')
        .insert({
          assistant_id: assistantId,
          file_id: fileData.id,
          openai_file_id: uploadedFile.id
        });

      if (assocError) {
        console.error('Error creating assistant file association:', assocError);
        throw assocError;
      }
    }

    return {
      id: fileData.id,
      file_name: fileData.file_name,
      file_type: fileData.file_type,
      file_size: fileData.file_size,
      status: fileData.status,
      openai_file_id: uploadedFile.id
    };
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

export const deleteFileFromAssistant = async (
  fileId: string,
  openaiFileId: string,
  assistantId?: string
) => {
  console.log('Deleting file:', { fileId, openaiFileId, assistantId });
  
  try {
    const openai = await getOpenAIClient();
    
    // Get the vector store ID for this file
    const { data: fileData } = await supabase
      .from('vector_store_files')
      .select('vector_store_id')
      .eq('id', fileId)
      .single();

    if (fileData?.vector_store_id) {
      // Get the OpenAI vector store ID
      const { data: vectorStore } = await supabase
        .from('vector_stores')
        .select('openai_vector_store_id')
        .eq('id', fileData.vector_store_id)
        .single();

      if (vectorStore?.openai_vector_store_id) {
        console.log('Deleting file from vector store:', vectorStore.openai_vector_store_id);
        
        await fetch(`https://api.openai.com/v1/vector_stores/${vectorStore.openai_vector_store_id}/files/${openaiFileId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${openai.apiKey}`,
            'OpenAI-Beta': 'assistants=v2'
          }
        });
      }
    }

    // Delete association from assistant if assistantId is provided
    if (assistantId) {
      const { error: assocError } = await supabase
        .from('assistant_files')
        .delete()
        .eq('file_id', fileId)
        .eq('assistant_id', assistantId);

      if (assocError) throw assocError;
    }

    // Delete file from OpenAI
    await openai.files.del(openaiFileId);
    console.log('File deleted from OpenAI');

    // Delete file metadata from our database
    const { error: fileError } = await supabase
      .from('vector_store_files')
      .delete()
      .eq('id', fileId);

    if (fileError) throw fileError;

    console.log('File deleted from database');
  } catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
};

// Function to get all files for an assistant
export const getAssistantFiles = async (assistantId: string): Promise<ExistingFile[]> => {
  try {
    // First get the OpenAI assistant ID
    const { data: assistant } = await supabase
      .from('assistants')
      .select('assistant_id, vector_store_id')
      .eq('id', assistantId)
      .single();
      
    if (!assistant) {
      throw new Error('Assistant not found');
    }
    
    // Get files from our database through the assistant_files table
    const { data: assistantFiles, error: assocError } = await supabase
      .from('assistant_files')
      .select(`
        file_id,
        openai_file_id,
        vector_store_files (
          id,
          file_name,
          file_type,
          file_size,
          status
        )
      `)
      .eq('assistant_id', assistantId);

    if (assocError) throw assocError;

    // Transform the data - fix the type issues by properly accessing each item in the array
    if (!assistantFiles || !Array.isArray(assistantFiles)) {
      return [];
    }

    return assistantFiles.map((af: any) => {
      const fileData = af.vector_store_files ?? {};
      return {
        id: fileData.id ?? '',
        file_name: fileData.file_name ?? '',
        file_type: fileData.file_type ?? '',
        file_size: fileData.file_size ?? 0,
        status: fileData.status ?? '',
        openai_file_id: af.openai_file_id ?? '',
      };
    });
  } catch (error) {
    console.error('Error getting assistant files:', error);
    return [];
  }
};
